/**
 * Auth storage using sessionStorage so the session is per-tab.
 * Opening the app in a new tab (same browser) will not have the token and will show the login page.
 */
const KEY_TOKEN = "token";
const KEY_USER = "user";
const KEY_EMAIL = "Email";
const KEY_USERNAME = "Username";
const KEY_ROLE = "Role";

function get(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function set(key, value) {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function remove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function getToken() {
  return get(KEY_TOKEN);
}

export function getUser() {
  const raw = get(KEY_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Company id from the post-selection session user (JWT-backed). */
export function getCompanyId() {
  const user = getUser();
  const id = user?.companyId || user?.CompanyId || null;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

export function getEmail() {
  return get(KEY_EMAIL);
}

export function getUsername() {
  return get(KEY_USERNAME);
}

export function getRole() {
  return get(KEY_ROLE);
}

function getRoleFromUser(user) {
  if (!user || typeof user !== "object") return "";
  const ur = user.UserRoles?.[0]?.RoleName ?? user.userRoles?.[0]?.roleName;
  if (ur) return String(ur).trim().toUpperCase();
  return String(user.Role ?? user.role ?? "").trim().toUpperCase();
}

/**
 * Merge fields into stored user (e.g. after GET /validate). Updates Role when UserRoles change.
 * @returns {boolean} true if write succeeded
 */
export function mergeStoredUser(partial) {
  if (!partial || typeof partial !== "object") return false;
  const cur = getUser() || {};
  const next = { ...cur, ...partial };
  if ((next.companyId == null || next.companyId === "") && cur.companyId) {
    next.companyId = cur.companyId;
  }
  if ((next.companyName == null || next.companyName === "") && cur.companyName) {
    next.companyName = cur.companyName;
  }
  if (next.companyIsDefault == null && cur.companyIsDefault != null) {
    next.companyIsDefault = cur.companyIsDefault;
  }
  let ok = set(KEY_USER, JSON.stringify(next));
  const role = getRoleFromUser(next);
  if (role) ok = set(KEY_ROLE, role) && ok;
  return ok;
}

/**
 * Set auth data after login. Pass an object with token, user (object), Email, Username, Role.
 * The user object from /select-company includes companyId — keep it intact.
 * @returns {boolean} true if all writes succeeded (e.g. not in restricted private mode)
 */
export function setAuth(data) {
  let ok = true;
  if (data.token != null) ok = set(KEY_TOKEN, data.token) && ok;
  if (data.user != null) {
    const user = { ...data.user };
    if (user.companyId == null && data.companyId != null) user.companyId = data.companyId;
    if (user.companyName == null && data.companyName != null) user.companyName = data.companyName;
    ok = set(KEY_USER, JSON.stringify(user)) && ok;
  }
  if (data.Email != null) ok = set(KEY_EMAIL, data.Email) && ok;
  if (data.Username != null) ok = set(KEY_USERNAME, data.Username) && ok;
  let role = data.Role != null ? String(data.Role).trim() : "";
  if (!role && data.user) role = getRoleFromUser(data.user);
  if (role) ok = set(KEY_ROLE, role) && ok;
  return ok;
}

export function clearAuth() {
  remove(KEY_TOKEN);
  remove(KEY_USER);
  remove(KEY_EMAIL);
  remove(KEY_USERNAME);
  remove(KEY_ROLE);
}
